import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { KPI_LOADING_DISPLAY } from '../../lib/managerial-kpis'
import type { ClientProfitabilityView } from '../../lib/client-profitability'
import styles from './ClientProfitabilityPanel.module.css'

interface ClientProfitabilityPanelProps {
  rows: ClientProfitabilityView[]
  isLoading: boolean
  isBusy?: boolean
  hasData?: boolean
}

const TONE_CLASS: Record<ClientProfitabilityView['tone'], string> = {
  amber: styles.toneAmber,
  ink: styles.toneInk,
  orange: styles.toneOrange,
  slate: styles.toneSlate,
  teal: styles.toneTeal,
}

export function ClientProfitabilityPanel({
  rows,
  isLoading,
  isBusy = false,
  hasData = true,
}: ClientProfitabilityPanelProps) {
  const showEmptyRecorte = hasData && rows.length === 0

  return (
    <section
      className={styles.card}
      aria-labelledby="client-profitability-title"
      aria-busy={isLoading || isBusy}
    >
      <div className={styles.header}>
        <h2 id="client-profitability-title" className={styles.title}>
          Rentabilidade por cliente
        </h2>
        <Link to="/home-drftadmin" className={styles.seeAll}>
          Ver todos
        </Link>
      </div>

      <p className={styles.scopeNote}>
        Todas as instituições com vendas reconhecidas, por GMV em todo o histórico,
        sem os filtros do topo ou corte mínimo de valor. Custos:
        WhatsApp, splits de terceiros e PIX. Receita da plataforma: parcela nos splits,
        antes das despesas operacionais. Take rate: receita da plataforma dividida pelo
        GMV.
      </p>

      {isLoading ? (
        <p className={styles.empty}>Carregando clientes...</p>
      ) : showEmptyRecorte ? (
        <p className={styles.empty}>Nenhum cliente com receita.</p>
      ) : rows.length === 0 ? (
        <p className={styles.empty}>{KPI_LOADING_DISPLAY}</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Cliente</th>
                <th scope="col" className={styles.numeric}>
                  GMV
                </th>
                <th scope="col" className={styles.numeric}>
                  Custos
                </th>
                <th scope="col" className={styles.numeric}>
                  Receita da plataforma
                </th>
                <th scope="col" className={styles.numeric}>
                  Take rate
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className={styles.client}>
                      <span
                        className={`${styles.avatar} ${TONE_CLASS[row.tone]}`}
                        aria-hidden="true"
                      >
                        <Icon icon={row.icon} />
                      </span>
                      <span className={styles.clientName}>{row.name}</span>
                    </span>
                  </td>
                  <td className={`${styles.numeric} ${styles.amount}`}>
                    {row.formattedRevenue}
                  </td>
                  <td className={`${styles.numeric} ${styles.costs}`}>
                    {row.formattedCosts}
                  </td>
                  <td className={`${styles.numeric} ${styles.amount}`}>
                    {row.formattedProfit}
                  </td>
                  <td className={styles.numeric}>
                    <span className={styles.margin}>
                      <span className={styles.marginValue}>{row.formattedMargin}</span>
                      <span className={styles.marginTrack} aria-hidden="true">
                        <span
                          className={styles.marginFill}
                          style={{ width: `${row.marginBarPercent}%` }}
                        />
                      </span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
